import {
  CHARGE_FAILURE_CATEGORY,
  CHARGE_FAILURE_STATUS,
  chargeFailureCategorySchema,
  chargeFailureStatusSchema,
  statusEntries,
  type ChargeFailureCategory,
  type ChargeFailureStatus,
} from '@flux/shared';
import type { JSX } from 'react';
import { z } from 'zod';
import { ListToolbar } from '@/components/data-table/list-toolbar.js';
import { MultiSelectFilter, PeriodFilter, type MultiSelectFilterOption } from './filter-controls.js';

/** Délai de la recherche par email (`pattern-lists-tables` §9). */
const SEARCH_DEBOUNCE_MS = 300;

export interface ChargeFailureFiltersValue {
  category: ChargeFailureCategory[];
  status: ChargeFailureStatus[];
  search: string;
  /** ISO-8601 UTC, début du jour parisien choisi. */
  from?: string;
  /** ISO-8601 UTC, fin du jour parisien choisi. */
  to?: string;
}

export type ChargeFailureFiltersPatch = Partial<{
  category: ChargeFailureCategory[] | undefined;
  status: ChargeFailureStatus[] | undefined;
  search: string | undefined;
  from: string | undefined;
  to: string | undefined;
}>;

/** Patch qui retire tous les filtres de l'onglet (« Réinitialiser »). */
export const CLEAR_CHARGE_FAILURE_FILTERS: ChargeFailureFiltersPatch = {
  category: undefined,
  status: undefined,
  search: undefined,
  from: undefined,
  to: undefined,
};

const CATEGORY_OPTIONS: readonly MultiSelectFilterOption<ChargeFailureCategory>[] = statusEntries(
  CHARGE_FAILURE_CATEGORY,
).map((entry) => ({ value: entry.code, label: entry.label }));

const STATUS_OPTIONS: readonly MultiSelectFilterOption<ChargeFailureStatus>[] = statusEntries(
  CHARGE_FAILURE_STATUS,
).map((entry) => ({ value: entry.code, label: entry.label }));

function readRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

const isoDateTime = z.iso.datetime();

/** Lit les filtres de l'onglet Paiements échoués depuis `useSearch({strict:false})` (`06` §8.1). */
export function readChargeFailureFilters(rawSearch: unknown): ChargeFailureFiltersValue {
  const search = readRecord(rawSearch);
  const category = z.array(chargeFailureCategorySchema).safeParse(search.category);
  const status = z.array(chargeFailureStatusSchema).safeParse(search.status);
  const term = z.string().trim().min(1).safeParse(search.search);
  const from = isoDateTime.safeParse(search.from);
  const to = isoDateTime.safeParse(search.to);
  return {
    category: category.success ? category.data : [],
    status: status.success ? status.data : [],
    search: term.success ? term.data : '',
    ...(from.success ? { from: from.data } : {}),
    ...(to.success ? { to: to.data } : {}),
  };
}

export function hasActiveChargeFailureFilters(value: ChargeFailureFiltersValue): boolean {
  return (
    value.category.length > 0 ||
    value.status.length > 0 ||
    value.search !== '' ||
    value.from !== undefined ||
    value.to !== undefined
  );
}

export interface ChargeFailureFiltersProps {
  value: ChargeFailureFiltersValue;
  onChange: (patch: ChargeFailureFiltersPatch) => void;
}

/**
 * Barre de filtres de l'onglet Paiements échoués (`06` §8.1) : recherche par
 * email, catégorie, statut, période.
 */
export function ChargeFailureFilters({ value, onChange }: ChargeFailureFiltersProps): JSX.Element {
  return (
    <ListToolbar
      search={{
        value: value.search,
        onChange: (next) => {
          onChange({ search: next === '' ? undefined : next });
        },
        placeholder: 'Rechercher un email…',
        label: 'Rechercher un email',
        debounceMs: SEARCH_DEBOUNCE_MS,
      }}
      isFiltered={hasActiveChargeFailureFilters(value)}
      onReset={() => {
        onChange(CLEAR_CHARGE_FAILURE_FILTERS);
      }}
    >
      <MultiSelectFilter
        label="Catégorie"
        ariaLabel="Filtrer par catégorie"
        options={CATEGORY_OPTIONS}
        value={value.category}
        onChange={(category) => {
          onChange({ category });
        }}
      />
      <MultiSelectFilter
        label="Statut"
        ariaLabel="Filtrer par statut"
        options={STATUS_OPTIONS}
        value={value.status}
        onChange={(status) => {
          onChange({ status });
        }}
      />
      <PeriodFilter
        from={value.from}
        to={value.to}
        fromLabel="Reçu depuis le"
        toLabel="Reçu jusqu’au"
        onChange={onChange}
      />
    </ListToolbar>
  );
}
